import React, { useState } from "react";
import {
	Card,
	CardContent,
	Typography,
	Box,
	Chip,
	Divider,
	Grid,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	CircularProgress,
} from "@mui/material";
import {
	TableBar,
	Person,
	AccessTime,
	CalendarToday,
	CheckCircle,
	EventAvailable,
} from "@mui/icons-material";
import { updateOrder } from "../../../../../../services/Admin/OrdersApi";

const TablesOrdersCard = ({ table, onTableClick, onOrderUpdated }) => {
	const order = table?.active_order;
	const hasActiveOrder = !!order;
	const isUnderReview = order?.status === "under_review";
	const [nextStatus, setNextStatus] = useState("pending");
	const [updatingStatus, setUpdatingStatus] = useState(false);

	if (!table) return null;

	// تحويل الوقت من 24 ساعة إلى 12 ساعة
	const convertTo12Hour = (time24) => {
		if (!time24) return "";
		const [hours, minutes] = time24.split(":");
		let hour = parseInt(hours);
		const ampm = hour >= 12 ? "م" : "ص";
		hour = hour % 12 || 12;
		return `${hour}:${minutes} ${ampm}`;
	};

	// حساب الإجمالي من مجموع الأصناف
	const calculateTotal = () => {
		if (!order?.order_items || order.order_items.length === 0) return 0;
		return order.order_items.reduce((sum, item) => {
			return sum + parseFloat(item.total || 0);
		}, 0);
	};

	// تحديد لون الحالة
	const getStatusColor = (status) => {
		switch (status) {
			case "under_review":
				return "secondary";
			case "pending":
				return "error";
			case "in_prepration":
			case "in_preparation":
				return "warning";
			case "pickup":
				return "success";
			case "out_of_preparation":
				return "info";
			case "finished":
				return "success";
			case "cancelled":
				return "error";
			default:
				return "default";
		}
	};

	// تحديد نص الحالة
	const getStatusText = (status) => {
		switch (status) {
			case "under_review":
				return "تحت المراجعة";
			case "pending":
				return "قيد التجهيز";
			case "in_prepration":
			case "in_preparation":
				return "في التحضير";
			case "pickup":
				return "تم الاستلام من العميل";
			case "out_of_preparation":
				return "خارج التحضير";
			case "finished":
				return "منتهي";
			case "cancelled":
				return "ملغي";
			default:
				return status;
		}
	};

	// تحديد مصدر الطلب
	const getSourceLabel = (type) => {
		switch (type) {
			case "client":
				return "تم من خلال العميل";
			case "local":
				return "تم من خلال كاشير";
			case "screen":
				return "تم من خلال شاشة";
			default:
				return null;
		}
	};

	const handleUpdateUnderReviewStatus = async (event) => {
		event.stopPropagation();
		if (!order?.id || !nextStatus) return;

		setUpdatingStatus(true);
		try {
			await updateOrder({
				order_id: order.id,
				status: nextStatus,
				table_id: order.table_id ?? table.id ?? null,
				captain_order_id: order.captain_order_id ?? null,
				delivery_id: order.delivery_id ?? null,
				date: order.date,
				order_type: order.order_type,
			});
			onOrderUpdated?.();
		} catch (error) {
			console.error("Failed to update under_review order status", error);
		} finally {
			setUpdatingStatus(false);
		}
	};

	return (
		<Card
			onClick={() => onTableClick && onTableClick(table)}
			sx={{
				mb: 1.5,
				boxShadow: 2,
				borderRadius: 1.5,
				border: "2px solid",
				borderColor: isUnderReview
					? "#9c27b0"
					: hasActiveOrder
					? order.is_client_order === 1
						? "#ff9800"
						: "#2196f3"
					: table.available === 1
					? "#4caf50"
					: "#9e9e9e",
				backgroundColor: isUnderReview
					? "#f8eafc"
					: hasActiveOrder
					? order.is_client_order === 1
						? "#fff8e1"
						: "#e3f2fd"
					: table.available === 1
					? "#e8f5e9"
					: "white",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				cursor: "pointer",
				transition: "all 0.3s",
				"&:hover": {
					transform: "translateY(-2px)",
					boxShadow: 4,
				},
			}}
		>
			<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 }, flex: 1 }}>
				{/* رأس الكارد - اسم الطاولة */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						mb: 1,
						gap: 0.5,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<TableBar sx={{ fontSize: 20, color: hasActiveOrder ? "primary.main" : "text.secondary" }} />
						<Typography variant="h6" fontWeight="bold">
							{table.name}
						</Typography>
					</Box>
					
					{hasActiveOrder ? (
						<Chip
							label="مشغولة"
							color="error"
							size="small"
							sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
						/>
					) : (
						<Chip
							icon={<EventAvailable sx={{ fontSize: 16 }} />}
							label="متاحة"
							color="success"
							size="small"
							sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
						/>
					)}
				</Box>

				{hasActiveOrder ? (
					<>
						{/* معلومات الطلب */}
						<Box sx={{ mb: 1 }}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<Typography variant="subtitle2" fontWeight="bold">
										طلب #{order.general_increment}
									</Typography>
									{order.is_client_order === 1 && (
										<Chip
											label="خارجي"
											size="small"
											color="warning"
											sx={{ height: 18, fontSize: "0.65rem", fontWeight: "bold" }}
										/>
									)}
								</Box>
								<Chip
									label={getStatusText(order.status)}
									color={getStatusColor(order.status)}
									size="small"
									sx={{ height: 18, fontSize: "0.65rem" }}
								/>
							</Box>
							{getSourceLabel(order.type) && (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.3, fontSize: "0.65rem" }}
								>
									{getSourceLabel(order.type)}
								</Typography>
							)}
							
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
								<Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<CalendarToday sx={{ fontSize: 12 }} />
									{order.date}
								</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<AccessTime sx={{ fontSize: 12 }} />
									{convertTo12Hour(order.open_time)}
								</Typography>
							</Box>

							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Box>
									<Typography variant="caption" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Person sx={{ fontSize: 14 }} />
										{order.customer?.name || "لا يوجد"}
									</Typography>
									{order.captain_order && (
										<Typography variant="caption" color="info.main" sx={{ display: "block", fontSize: "0.65rem", mt: 0.3 }}>
											👤 الكابتن: {order.captain_order.name}
										</Typography>
									)}
								</Box>
								<Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ fontSize: "0.9rem" }}>
									{calculateTotal().toFixed(2)} ج.م
								</Typography>
							</Box>
						</Box>

						<Divider sx={{ my: 1 }} />

						{/* إحصائيات الأصناف */}
						<Box>
							{order.order_items && order.order_items.length > 0 ? (
								<Grid container spacing={0.5}>
									<Grid item xs={6}>
										<Box
											sx={{
												p: 0.5,
												backgroundColor: "#f5f5f5",
												borderRadius: 1,
												textAlign: "center",
											}}
										>
											<Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: "0.85rem", lineHeight: 1.2 }}>
												{order.order_items.length}
											</Typography>
											<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
												إجمالي
											</Typography>
										</Box>
									</Grid>
									<Grid item xs={6}>
										<Box
											sx={{
												p: 0.5,
												backgroundColor: "#fff3e0",
												borderRadius: 1,
												textAlign: "center",
											}}
										>
											<Typography variant="body2" fontWeight="bold" color="error.main" sx={{ fontSize: "0.85rem", lineHeight: 1.2 }}>
												{order.order_items.filter((item) => item.status === "pending").length}
											</Typography>
											<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
												انتظار
											</Typography>
										</Box>
									</Grid>
									<Grid item xs={6}>
										<Box
											sx={{
												p: 0.5,
												backgroundColor: "#e3f2fd",
												borderRadius: 1,
												textAlign: "center",
											}}
										>
											<Typography variant="body2" fontWeight="bold" color="info.main" sx={{ fontSize: "0.85rem", lineHeight: 1.2 }}>
												{order.order_items.filter((item) => 
													item.status === "preparing" || 
													item.status === "in_progress" ||
													item.status === "in_preparation"
												).length}
											</Typography>
											<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
												تحضير
											</Typography>
										</Box>
									</Grid>
									<Grid item xs={6}>
										<Box
											sx={{
												p: 0.5,
												backgroundColor: "#e8f5e9",
												borderRadius: 1,
												textAlign: "center",
											}}
										>
											<Typography variant="body2" fontWeight="bold" color="success.main" sx={{ fontSize: "0.85rem", lineHeight: 1.2 }}>
												{order.order_items.filter((item) => item.status === "finished").length}
											</Typography>
											<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
												منتهي
											</Typography>
										</Box>
									</Grid>
								</Grid>
							) : (
								<Typography variant="caption" color="text.secondary">
									لا توجد عناصر
								</Typography>
							)}
						</Box>

						<Divider sx={{ my: 1 }} />

						{isUnderReview && (
							<>
								<Box
									sx={{
										display: "flex",
										gap: 1,
										alignItems: "center",
										mb: 1,
									}}
									onClick={(event) => event.stopPropagation()}
								>
									<FormControl size="small" fullWidth>
										<InputLabel>تغيير الحالة</InputLabel>
										<Select
											value={nextStatus}
											label="تغيير الحالة"
											onChange={(event) => setNextStatus(event.target.value)}
											disabled={updatingStatus}
										>
											<MenuItem value="pending">قبول وتجهيز</MenuItem>
											<MenuItem value="canceled">الغاء</MenuItem>
										</Select>
									</FormControl>

									<Button
										variant="contained"
										size="small"
										onClick={handleUpdateUnderReviewStatus}
										disabled={updatingStatus}
										sx={{ minWidth: 92 }}
									>
										{updatingStatus ? <CircularProgress size={16} color="inherit" /> : "تحديث"}
									</Button>
								</Box>
								<Divider sx={{ my: 1 }} />
							</>
						)}

						{/* معلومات إضافية */}
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
								{order.user?.name || ""}
							</Typography>
							<Box sx={{ display: "flex", gap: 1 }}>
								<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
									ي:{order.daily_increment}
								</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
									ش:{order.monthly_increment}
								</Typography>
							</Box>
						</Box>
					</>
				) : (
					<Box sx={{ textAlign: "center", py: 2 }}>
						<CheckCircle sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
						<Typography variant="body2" color="success.main" fontWeight="bold">
							الطاولة متاحة
						</Typography>
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
							لا يوجد طلب نشط
						</Typography>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

export default TablesOrdersCard;
